import React, { useEffect, useRef, useState } from 'react';
import axios from '../utils/axiosConfig';
import DateInput from '../components/DateInput';
import { formatCurrency } from '../utils/currency';
import { formatDate, parseToDate, toIsoDate, todayIso } from '../utils/dates';
import { printHtmlElement } from '../utils/printHtml';
import './Products.css';
import './CustomerReport.css';
import './GstReport.css';

const firstOfMonth = () => {
  const d = new Date();
  return toIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};

const GSTR1_PRINT_CSS = `
@page { size: A4 landscape; margin: 8mm; }
@media print {
  @page { size: A4 landscape; margin: 8mm; }
  html, body { background: #fff !important; }
  .gstr1-sheet { width: 100% !important; margin: 0 !important; padding: 0 !important; }
}
`;

const monthYear = (iso) => {
  const d = parseToDate(iso);
  if (!d) return '';
  return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
};

const formatAmt = (value) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n - Math.round(n)) < 0.001) {
    return Math.round(n).toLocaleString('en-IN');
  }
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRate = (value) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return String(Math.round(n * 100) / 100);
};

const GstReport = () => {
  const sheetRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [seller, setSeller] = useState({ gstin: '', legal_name: '', trade_name: '' });
  const [invoices, setInvoices] = useState([]);
  const [filters, setFilters] = useState({
    from: firstOfMonth(),
    to: todayIso(),
    search: '',
    dummy: false
  });

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async (nextFilters = filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (nextFilters.from) params.set('from', nextFilters.from);
      if (nextFilters.to) params.set('to', nextFilters.to);
      if (nextFilters.search) params.set('search', nextFilters.search);
      if (nextFilters.dummy) params.set('dummy', '1');
      const res = await axios.get(`/api/reports/gst?${params.toString()}`);
      setSummary(res.data.summary || null);
      setSeller(res.data.seller || { gstin: '', legal_name: '', trade_name: '' });
      setInvoices(res.data.invoices || []);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Failed to load GST report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchReport(filters);
  };

  const clearFilters = () => {
    const reset = { from: firstOfMonth(), to: todayIso(), search: '', dummy: filters.dummy };
    setFilters(reset);
    fetchReport(reset);
  };

  const periodLabel = [monthYear(filters.from), monthYear(filters.to)].filter(Boolean).join(' - ')
    || 'Selected period';

  const csvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const downloadCsv = () => {
    const header = [
      'GSTIN/UIN',
      'Invoice No.',
      'Date',
      'Value',
      'Rate',
      'Cess Rate',
      'Taxable value',
      'Integrated Tax',
      'Central Tax',
      'State/UT Tax',
      'Cess',
      'Place of Supply'
    ];
    const lines = [
      header.join(','),
      ...invoices.map((row) => [
        csvValue(row.customer_gstin),
        csvValue(row.invoice_number),
        formatDate(row.issue_date, ''),
        row.total_amount,
        row.tax_rate,
        row.cess_rate || 0,
        row.taxable,
        row.igst_amount || 0,
        row.cgst_amount,
        row.sgst_amount,
        row.cess_amount || 0,
        csvValue(row.place_of_supply)
      ].join(','))
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const prefix = filters.dummy ? 'gstr1-dummy' : 'gstr1';
    a.download = `${prefix}-${filters.from || 'start'}-to-${filters.to || 'end'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    printHtmlElement(sheetRef.current, {
      title: filters.dummy ? 'GSTR1 Report — Invoices' : 'GSTR1 Report',
      extraCss: GSTR1_PRINT_CSS,
      pageWidth: '297mm',
      pageHeight: '210mm'
    });
  };

  if (loading && !summary) {
    return (
      <div className="page-container">
        <div className="container">Loading...</div>
      </div>
    );
  }

  const gstr1Sheet = (
    <div className="gstr1-sheet" ref={sheetRef}>
      <h1 className="gstr1-title">{filters.dummy ? 'GSTR1 Report — Invoices' : 'GSTR1 Report'}</h1>
      <table className="gstr1-meta">
        <tbody>
          <tr>
            <td>1. GSTIN</td>
            <td>{seller.gstin || ''}</td>
            <td className="gstr1-period-label">Period</td>
            <td>{periodLabel}</td>
          </tr>
          <tr>
            <td>2.a Legal name of the registered person</td>
            <td colSpan="3">{seller.legal_name || ''}</td>
          </tr>
          <tr>
            <td>2.b Trade name, if any</td>
            <td colSpan="3">{seller.trade_name || ''}</td>
          </tr>
        </tbody>
      </table>

      <h2 className="gstr1-sale-title">Sale</h2>
      <table className="gstr1-table">
        <thead>
          <tr>
            <th rowSpan="2">GSTIN/<br />UIN</th>
            <th colSpan="3">Invoice details</th>
            <th rowSpan="2">Rate</th>
            <th rowSpan="2">Cess<br />Rate</th>
            <th rowSpan="2">Taxable<br />value</th>
            <th colSpan="4">Amount</th>
            <th rowSpan="2">Place of<br />Supply (Name<br />of State)</th>
          </tr>
          <tr>
            <th>No.</th>
            <th>Date</th>
            <th>Value</th>
            <th>Integrated<br />Tax</th>
            <th>Central<br />Tax</th>
            <th>State/UT<br />Tax</th>
            <th>Cess</th>
          </tr>
        </thead>
        <tbody>
          {invoices.length === 0 ? (
            <tr>
              <td colSpan="12" className="gstr1-empty">
                {filters.dummy
                  ? 'No dummy GST bills found for the selected period'
                  : 'No GST bills found for the selected period'}
              </td>
            </tr>
          ) : (
            invoices.map((row) => (
              <tr key={row.id}>
                <td>{row.customer_gstin || ''}</td>
                <td>{row.invoice_number}</td>
                <td>{formatDate(row.issue_date, '')}</td>
                <td className="num">{formatAmt(row.total_amount)}</td>
                <td className="num">{formatRate(row.tax_rate)}</td>
                <td className="num">{formatRate(row.cess_rate)}</td>
                <td className="num">{formatAmt(row.taxable)}</td>
                <td className="num">{formatAmt(row.igst_amount)}</td>
                <td className="num">{formatAmt(row.cgst_amount)}</td>
                <td className="num">{formatAmt(row.sgst_amount)}</td>
                <td className="num">{formatAmt(row.cess_amount)}</td>
                <td>{row.place_of_supply || ''}</td>
              </tr>
            ))
          )}
        </tbody>
        {summary && invoices.length > 0 ? (
          <tfoot>
            <tr>
              <th colSpan="3">Total ({summary.invoice_count} bills)</th>
              <th className="num">{formatAmt(summary.total_amount)}</th>
              <th></th>
              <th></th>
              <th className="num">{formatAmt(summary.taxable)}</th>
              <th className="num">0</th>
              <th className="num">{formatAmt(summary.cgst_amount)}</th>
              <th className="num">{formatAmt(summary.sgst_amount)}</th>
              <th className="num">0</th>
              <th></th>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );

  return (
    <div className="page-container customer-report-page">
      <div className="container">
        <div className="page-header no-print">
          <h1>GST Report</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={downloadCsv} disabled={!invoices.length}>
              CSV
            </button>
            <button type="button" className="btn btn-primary" onClick={handlePrint} disabled={!invoices.length}>
              Print
            </button>
          </div>
        </div>

        <div className="card no-print" style={{ marginBottom: '16px' }}>
          <div className="gst-report-tabs">
            <button
              type="button"
              className={`gst-report-tab${!filters.dummy ? ' active' : ''}`}
              onClick={() => {
                if (filters.dummy) {
                  const next = { ...filters, dummy: false };
                  setFilters(next);
                  fetchReport(next);
                }
              }}
            >
              GST invoices
            </button>
            <button
              type="button"
              className={`gst-report-tab${filters.dummy ? ' active' : ''}`}
              onClick={() => {
                if (!filters.dummy) {
                  const next = { ...filters, dummy: true };
                  setFilters(next);
                  fetchReport(next);
                }
              }}
            >
              Dummy invoices
            </button>
          </div>
          <form onSubmit={applyFilters} className="report-filters gst-report-filters">
            <div className="form-group">
              <label>From</label>
              <DateInput
                value={filters.from}
                onChange={(e) => handleFilterChange('from', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>To</label>
              <DateInput
                value={filters.to}
                onChange={(e) => handleFilterChange('to', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Invoice, customer or GSTIN"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="report-filter-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Loading...' : 'Apply'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                Reset
              </button>
            </div>
          </form>
          <p className="report-help">
            {filters.dummy
              ? 'Dummy invoices only. These bills are not included in the GST invoices report.'
              : 'GSTR-1 format. Only invoices created with GST on. Dummy and cancelled bills are excluded.'}
            {' '}Customer GSTIN uses Tax ID; Place of Supply uses customer State.
          </p>
        </div>

        {summary && (
          <div className="report-summary-grid gst-summary-grid no-print">
            <div className="report-summary-card">
              <span>{filters.dummy ? 'Dummy GST bills' : 'GST bills'}</span>
              <strong>{summary.invoice_count}</strong>
            </div>
            <div className="report-summary-card">
              <span>Taxable</span>
              <strong>{formatCurrency(summary.taxable)}</strong>
            </div>
            <div className="report-summary-card">
              <span>CGST</span>
              <strong>{formatCurrency(summary.cgst_amount)}</strong>
            </div>
            <div className="report-summary-card">
              <span>SGST</span>
              <strong>{formatCurrency(summary.sgst_amount)}</strong>
            </div>
            <div className="report-summary-card debit">
              <span>Total GST</span>
              <strong>{formatCurrency(summary.tax_amount)}</strong>
            </div>
            <div className="report-summary-card credit">
              <span>Invoice total</span>
              <strong>{formatCurrency(summary.total_amount)}</strong>
            </div>
          </div>
        )}

        <div className="card gstr1-card">
          {gstr1Sheet}
        </div>
      </div>
    </div>
  );
};

export default GstReport;
